const defaultOptions = {
    chunks: 200,
    log: false,
    from: 0,
    to: 1,
    isFixed: false,
};
/**
 * Solves a whole series of related equilibria.
 *
 * Each point is warm-started from the previous solution, which is both much
 * faster and much more stable than solving every point from scratch.
 */
export class Serie {
    helper;
    constructor(helper) {
        this.helper = helper;
    }
    /**
     * Titrate one solution, or a mixture of them, with another.
     * @param options - The solutions and the number of points.
     * @returns The titration curve and the speciation at every point.
     */
    getTitration(options) {
        const { chunks, ...rest } = { ...defaultOptions, ...options };
        const helper = this.helper.clone();
        helper.resetSpecies();
        helper.setOptions(rest);
        const analytes = Array.isArray(options.solution)
            ? options.solution
            : [options.solution];
        let analyteVolume = 0;
        const analyteQuantities = new Map();
        for (const analyte of analytes) {
            analyteVolume += analyte.volume;
            analyteQuantities.set(analyte.type, (analyteQuantities.get(analyte.type) ?? 0) +
                analyte.concentration * analyte.volume);
        }
        const titrantConcentration = options.titrationSolution.concentration;
        const titrantVolume = options.titrationSolution.volume;
        for (const type of analyteQuantities.keys())
            helper.addSpecie(type);
        helper.addSpecie(options.titrationSolution.type);
        const volumes = [];
        const solutions = [];
        const xy = [];
        let errorCount = 0;
        let previous = null;
        for (let i = 0; i <= chunks; i++) {
            const volume = (titrantVolume * i) / chunks;
            helper.setTotal(options.titrationSolution.type, volume * titrantConcentration);
            for (const [type, quantity] of analyteQuantities) {
                helper.setTotal(type, quantity);
            }
            helper.setOptions({ volume: volume + analyteVolume });
            const equilibrium = helper.getEquilibrium();
            if (previous) {
                equilibrium.setInitial(previous);
                previous = equilibrium.solve();
            }
            else {
                previous = equilibrium.solveRobust();
            }
            if (previous) {
                solutions.push(previous);
                volumes.push(volume);
                xy.push(volume, -Math.log10(previous['H+']));
            }
            else {
                errorCount++;
            }
        }
        return {
            xy,
            errorCount,
            solutions,
            species: solutions[0] ? Object.keys(solutions[0]) : [],
            volumes,
            equations: helper.getEquations({ filtered: true }),
        };
    }
    /**
     * Sweep one species and solve the equilibrium at every value of it.
     *
     * With `varying: 'H+'`, `isFixed: true` and `log: true` this produces the
     * classic speciation diagram against pH.
     * @param options - What to sweep, over which range, and how finely.
     * @returns The swept values and the speciation at each of them.
     * @throws {Error} When `to` is not greater than `from`, or `varying` is missing.
     */
    getSolutions(options) {
        const merged = { ...defaultOptions, ...options };
        const { chunks, from, to, log, isFixed, varying, ...rest } = merged;
        if (from >= to) {
            throw new Error('Invalid arguments: property "to" should be larger than "from"');
        }
        if (!varying) {
            throw new Error('Invalid arguments: property "varying" is not defined');
        }
        const helper = this.helper.clone();
        helper.setOptions(rest);
        const x = [];
        const solutions = [];
        let errorCount = 0;
        let previous = null;
        for (let i = 0; i <= chunks; i++) {
            const value = from + ((to - from) * i) / chunks;
            const amount = log ? 10 ** -value : value;
            if (isFixed) {
                helper.setAtEquilibrium(varying, amount);
            }
            else {
                helper.setTotal(varying, amount);
            }
            const equilibrium = helper.getEquilibrium();
            if (previous) {
                equilibrium.setInitial(previous);
                previous = equilibrium.solve();
            }
            else {
                previous = equilibrium.solveRobust();
            }
            if (previous) {
                x.push(value);
                solutions.push(previous);
            }
            else {
                errorCount++;
            }
        }
        return {
            x,
            solutions,
            errorCount,
            species: solutions[0] ? Object.keys(solutions[0]) : [],
        };
    }
}
//# sourceMappingURL=Serie.js.map